import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { APP_CONFIG, AppConfig } from 'src/modules/main/providers/app.config';
import { CookieValue, run, show } from 'vanilla-cookieconsent';

import { CookieConsentConfigBuilder } from './cookie-consent-config.builder';
import type { CookieCategory, UserConsentValue } from './types';

@Injectable({
    providedIn: 'root',
})
export class CookieConsentService {
    private configBuilder: CookieConsentConfigBuilder;

    public readonly userConsent$ = new Subject<UserConsentValue>();

    constructor(
        @Inject(APP_CONFIG)
        private readonly appConfig: AppConfig,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {
        this.configBuilder = CookieConsentConfigBuilder.create();
    }

    get canShowCookieConsent() {
        return this.appConfig.COOKIE_CONSENT === true;
    }

    public init() {
        const config = this.configBuilder
            .addTranslation('en', './assets/locales/en.json')
            .addAnalytics({
                id: 'nreum',
                label: 'New Relic Browser Agent',
                cookies: [
                    {
                        name: /^(jsessionid|nragent|nreum)$/i,
                    },
                ],
            })
            .addAnalytics({
                id: 'faro',
                label: 'Grafana Faro (performance monitoring)',
                // Faro runs cookieless (per-tab session only); consent gates behavioral RUM signals.
                cookies: [],
            })
            .addAnalytics({
                id: 'hseum',
                label: 'HubSpot',
                cookies: [
                    {
                        name: /^(__cf_|__hs|hubspot)/i,
                    },
                ],
                // https://developers.hubspot.com/docs/api/events/cookie-banner
                // ^ doesn't work at all
                onAccept: () => {
                    const el = this.document.querySelector('[id^="hs-"][id$="-confirmation-button"]');
                    if (el) {
                        (el as HTMLElement).click();
                    }
                },
                onReject: () => {
                    const el = this.document.querySelector('[id^="hs-"][id$="-decline-button"]');
                    if (el) {
                        (el as HTMLElement).click();
                    }
                },
            })
            .addCallback('onConsent', ({ cookie }) => {
                this.emitConsentChanged(cookie);
            })
            .addCallback('onChange', ({ cookie, changedCategories: _cc, changedServices: _cs }) => {
                this.emitConsentChanged(cookie);
            })
            .addAutoShow(this.canShowCookieConsent)
            .build();

        run(config);
    }

    public showConsentModal() {
        if (this.canShowCookieConsent) {
            // Explicit true is required for "re-openings" of modal
            show(true);
        }
    }

    private emitConsentChanged(cookie: CookieValue) {
        const containsCategory = (category: CookieCategory) => cookie.categories.includes(category);

        const nextValue = {
            necessary: true,
            analytics: containsCategory('analytics'),
            marketing: containsCategory('marketing'),
            preference: containsCategory('preference'),
            services: cookie.services as UserConsentValue['services'],
        } as const;

        this.userConsent$.next(nextValue);
    }
}
